/* eslint-disable react/prop-types */
import { useState, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import usePermissions from "hooks/usePermissions";
import useTableRequest from "hooks/useTableRequest";
import DataTable from "components/organisms/DataTable/DataTable";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";

// import component
import Button from "components/atoms/Button/Button";
import getLabel from "helpers/getLabel";
import LocationFilter from "components/molecules/LocationFilter/LocationFilter";
import { getContactBook } from "api";
import testIds from "constants/testIds";
import EditContactList from "./EditContactList";

const AddressBook = function () {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const { company_shipper_settings: settings } = JSON.parse(permissions);

  const [showContactDialog, setShowContactDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const columns = useMemo(
    () => [
      {
        Header: (
          <span className="pl-9 text-nowrap">
            <FormattedMessage id="settings.contact-list.companyName" defaultMessage="Company Name" />
          </span>
        ),
        sortable: true,
        accessor: "company", // accessor is the "key" in the data
        maxWidth: 100,
        Cell: ({ value }) => (
          <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
            <p
              style={{
                margin: 0,
                padding: "8px",
                fontWeight: "bold",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {value}
            </p>
          </div>
        ),
      },
      {
        Header: (
          <span className="pl-9  text-nowrap">
            {intl.formatMessage(
              {
                id: "general.paymentLocation",
                defaultMessage: "PAYMENT LOCATION",
              },
              {
                paymentLocation: getLabel("Payment location", "uppercase"),
              },
            )}
          </span>
        ),
        sortable: true,
        accessor: "payment_location",
        Cell: ({ value }) => {
          const locationName = value.name || "-";
          return <p className="pl-9 pt-8 pb-8 mb-0">{locationName}</p>;
        },
      },
      {
        Header: (
          <span className="">
            <FormattedMessage id="general.name" defaultMessage="Name" />
          </span>
        ),
        sortable: true,
        accessor: "full_name",
        maxWidth: 100,
        Cell: ({ value }) => (
          <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
            <p
              style={{
                margin: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {value}
            </p>
          </div>
        ),
      },
      {
        Header: (
          <span>
            <FormattedMessage id="general.email" defaultMessage="Email" />
          </span>
        ),
        sortable: false,
        accessor: "email",
        Cell: ({ value }) => <p className="pt-8 pb-8 mb-0">{value}</p>,
      },
      {
        Header: (
          <span>
            <FormattedMessage id="new-request.schedule.pickupContactPhone" defaultMessage="Phone" />
          </span>
        ),
        sortable: false,
        accessor: "phone",
        Cell: ({ value }) => <p className="pt-8 pb-8 mb-0">{value}</p>,
      },
    ],
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const { instance, fetching, totalData, refetch } = useTableRequest(getContactBook, columns, {
    hiddenColumns: settings.contact_availability !== "Payment Location" ? ["payment_location"] : [],
    params: {},
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      ...(globalFilter.payment_location?.length && { payment_location: globalFilter.payment_location }),
    }),
  });

  const openContactDialog = (id) => {
    setSelectedId(id);
    setShowContactDialog(true);
  };

  const closeContactDialog = () => {
    setSelectedId(null);
    setShowContactDialog(false);
  };

  const handleFilterSelect = (value) => {
    instance.setGlobalFilter({
      ...instance.state.globalFilter,
      payment_location: value,
    });
  };

  return (
    <>
      <EditContactList
        refetch={refetch}
        showContactDialog={showContactDialog}
        closeContactDialog={closeContactDialog}
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        allowPaymentLocation={settings.contact_availability === "Payment Location"}
      />

      <DataTable
        tableInstance={instance}
        totalData={totalData}
        isLoading={fetching}
        draggable={false}
        onRowClick={(row) => openContactDialog(row.original.id)}
        headerRight={
          <div className="d-flex justify-content-between mt-3 w-100">
            <div>
              <GlobalTableSearch
                currentFilters={instance.state.globalFilter}
                setFilters={instance.setGlobalFilter}
                filterKey="query"
                eventContext="Address Book"
              />
            </div>
            <div className="d-flex align-items-center">
              {settings.contact_availability === "Payment Location" && <LocationFilter onChange={handleFilterSelect} />}
              <Button
                className="ml-3 rounded-pill"
                style={{ height: "26px" }}
                onClick={() => openContactDialog(null)}
                data-testid={testIds.ADDRESS_BOOK_ADD_BUTTON}
              >
                <FormattedMessage id="settings.contact-list.addContact" defaultMessage="Add contact" />
              </Button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default AddressBook;
